import { Injectable, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { IdentificationRequestStates } from '../model/identification_request_states';
import { IdentificationRequestStatus } from '../model/identification_request_status';
import { ObPopUpService } from '@oblique/oblique';
import { MatSort } from '@angular/material/sort';
import { IdentificationRequestStatusTips } from '../model/identification_request_status_tips';
import { getAcrLabel } from "../model/identification_request_acr_tips";
import { IdentificationType } from "../model/identification_request_payload";
import { VerificationService } from '../verification/verification.service';

@Injectable({ providedIn: 'root' })
export class VerificationStatusDataSource {
	@ViewChild(MatSort) sort!: MatSort;

	dataSource = new MatTableDataSource<IdentificationRequestStatus>([]);

	readonly columns: { key: string; cell: (element: IdentificationRequestStatus) => string }[] = [
		{ key: 'requesttime', cell: (element: IdentificationRequestStatus) => `${element.requesttime.toLocaleString('en-GB')}` },
		{ key: 'status', cell: (element: IdentificationRequestStatus) => `${IdentificationRequestStates[element.status]}` },
		{ key: 'jwtProp0a', cell: (element: IdentificationRequestStatus) => `${element.jwtProp0a ?? '-'}` },
		{ key: 'jwtProp0b', cell: (element: IdentificationRequestStatus) => `${element.jwtProp0b ?? '-'}` },
		{ key: 'jwtProp3', cell: (element: IdentificationRequestStatus) => `${element.jwtProp3 ?? '-'}` },
		{ key: 'remoteid', cell: (element: IdentificationRequestStatus) => `${element.remoteid}` },
		{ key: 'requestid', cell: (element: IdentificationRequestStatus) => `${element.requestid}` },
		{ key: 'jwtProp1', cell: (element: IdentificationRequestStatus) => `${element.jwtProp1 ?? '-'}` },
		{ key: 'jwtProp2', cell: (element: IdentificationRequestStatus) => `${element.jwtProp2 ?? '-'}` }
	];

	private readonly MMMI_PAST_REQUEST: string = 'mmmi_past_requests';

	constructor(private readonly popupService: ObPopUpService) {
		const pastRequestData = sessionStorage.getItem(this.MMMI_PAST_REQUEST);
		if (pastRequestData != null) {
			const data: IdentificationRequestStatus[] = JSON.parse(pastRequestData);
			data.forEach(row => (row.requesttime = new Date(row.requesttime)));
			this.dataSource.data = data;
		}
	}

	get displayedColumns(): string[] {
		return this.columns.map(columnDef => columnDef.key);
	}

	getUserName(row: IdentificationRequestStatus): string | undefined {
		return row.jwtProp1;
	}

	initVerificationRequest(id: string, verificationId: string, type: IdentificationType) {
		console.info(`created new verification request entry [ver-id: ${verificationId}]`);
		const data = this.dataSource.data;
		data.unshift({
			status: IdentificationRequestStates.NEW,
			tip: IdentificationRequestStatusTips[IdentificationRequestStatusTips.new],
			remoteid: id,
			requestid: verificationId,
			requesttime: new Date(),
			acrTip: "",
			type: type
		});
		this.dataSource = new MatTableDataSource<IdentificationRequestStatus>(data);
		sessionStorage.setItem(this.MMMI_PAST_REQUEST, JSON.stringify(data));
	}

	updateVerificationRequest(
		verificationId: string,
		type: IdentificationType,
		status: IdentificationRequestStates,
		tip: IdentificationRequestStatusTips,
		claim0a?: string,
		claim0b?: string,
		claim1?: string,
		claim2?: string,
		claim3?: string
	) {
		console.info(`updating status [ver-id: ${verificationId}; new-status: ${IdentificationRequestStates[status]}]`);
		const data = this.dataSource.data;
		const ind = data.findIndex((req: IdentificationRequestStatus) => req.requestid === verificationId && req.type === type);
		if (0 <= ind && ind < data.length) {
			data[ind].status = status;
			data[ind].tip = IdentificationRequestStatusTips[tip];
			data[ind].jwtProp0a = claim0a;
			data[ind].jwtProp0b = claim0b;
			data[ind].jwtProp1 = claim1;
			data[ind].acrTip = claim2;
			data[ind].jwtProp2 = status == IdentificationRequestStates.PENDING ? "..." : getAcrLabel(claim2);
			data[ind].jwtProp3 = claim3 ?? '-';
		}
		this.dataSource = new MatTableDataSource<IdentificationRequestStatus>(data);
		sessionStorage.setItem(this.MMMI_PAST_REQUEST, JSON.stringify(data));
	}

	removeRows(): void {
		if (this.popupService.confirm('Delete data?\nThis action will delete all previous verification requests.\nDo you want to proceed?')) {
			console.info(`deleting ${this.dataSource.data.length} entries`);
			this.dataSource = new MatTableDataSource<IdentificationRequestStatus>([]);
			sessionStorage.removeItem(this.MMMI_PAST_REQUEST);
			sessionStorage.removeItem(VerificationService.MMMI_REQUESTS_PENDING);
			sessionStorage.removeItem(VerificationService.MMMI_REQUESTS_HANDLED);
		}
	}
}
